import { apiWithBody, apiWithQuery, ModuleFE_BaseApi, ModuleFE_XHR, readFileContent, StorageKey, ThunderDispatcher } from '@nu-art/thunderstorm-frontend/index';
import { HeaderKey_DeviceId, HeaderKey_TabId } from '@nu-art/thunderstorm-shared';
import { dispatcher_onAuthRequired } from '@nu-art/thunderstorm-shared/no-auth-listener';
import { ApiDef_Account, ApiDef_SAML, DBDef_Accounts, QueryParam_SessionId } from '@nu-art/user-account-shared';
import { SessionKeyFE_Account, StorageKey_DeviceId, StorageKey_TabId } from './consts.js';
import { asArray, cloneObj, composeUrl, Exception, generateHex, KB } from '@nu-art/ts-common';
import { ModuleFE_Session } from '../session/ModuleFE_Session.js';
export var LoggedStatus;
(function (LoggedStatus) {
    LoggedStatus[LoggedStatus["VALIDATING"] = 0] = "VALIDATING";
    LoggedStatus[LoggedStatus["LOGGED_OUT"] = 1] = "LOGGED_OUT";
    LoggedStatus[LoggedStatus["SESSION_TIMEOUT"] = 2] = "SESSION_TIMEOUT";
    LoggedStatus[LoggedStatus["LOGGED_IN"] = 3] = "LOGGED_IN";
})(LoggedStatus || (LoggedStatus = {}));
export const dispatch_onLoginStatusChanged = new ThunderDispatcher('__onLoginStatusUpdated');
export const dispatch_onAccountsUpdated = new ThunderDispatcher('__onAccountsUpdated');
const StorageKey_PasswordAssertionConfig = new StorageKey('account__password-assertion-config', false);
class ModuleFE_Account_Class extends ModuleFE_BaseApi {
    _v1;
    status = LoggedStatus.LOGGED_OUT;
    __onLoginStatusUpdated() {
        //Get the password assertion config if needed
        if ([LoggedStatus.LOGGED_OUT, LoggedStatus.SESSION_TIMEOUT].includes(this.status))
            this._v1.getPasswordAssertionConfig({}).executeSync();
    }
    constructor() {
        super(DBDef_Accounts, dispatch_onAccountsUpdated);
        // const login = apiWithBody(ApiDef_Account._v1.login, this.setLoginInfo);
        this._v1 = {
            refreshSession: apiWithQuery(ApiDef_Account._v1.refreshSession),
            registerAccount: apiWithBody(ApiDef_Account._v1.registerAccount),
            createAccount: apiWithBody(ApiDef_Account._v1.createAccount, this.onAccountCreated),
            changePassword: apiWithBody(ApiDef_Account._v1.changePassword),
            login: apiWithBody(ApiDef_Account._v1.login),
            // login: (account: Account_Login['request']) => {
            //
            // 	toUpsert = this.cleanUp(toUpsert);
            // 	this.validateInternal(toUpsert);
            // 	return this.updatePending(toUpsert as DB_BaseObject, upsert(toUpsert), 'upsert');
            // },
            logout: apiWithQuery(ApiDef_Account._v1.logout),
            createToken: apiWithBody(ApiDef_Account._v1.createToken),
            setPassword: apiWithBody(ApiDef_Account._v1.setPassword),
            getSessions: apiWithQuery(ApiDef_Account._v1.getSessions),
            changeThumbnail: apiWithBody(ApiDef_Account._v1.changeThumbnail, this.onThumbnailChanged),
            loginSaml: apiWithQuery(ApiDef_SAML._v1.loginSaml, this.onLoginCompletedSAML),
            assertSAML: apiWithBody(ApiDef_SAML._v1.assertSAML),
            getPasswordAssertionConfig: apiWithQuery(ApiDef_Account._v1.getPasswordAssertionConfig, this.onPasswordAssertionConfig),
            deleteAccount: apiWithQuery(ApiDef_Account._v1.deleteAccount, async (response) => {
                await this.onEntryDeleted(response.account);
            }),
        };
    }
    init() {
        super.init();
        let defaultTabId = StorageKey_TabId.get();
        let defaultDeviceId = StorageKey_DeviceId.get();
        if (!defaultDeviceId) {
            defaultDeviceId = generateHex(32);
            console.log(`Defining new device Id: ${defaultDeviceId}`);
            StorageKey_DeviceId.set(defaultDeviceId);
        }
        if (!defaultTabId) {
            defaultTabId = generateHex(32);
            console.log(`Defining new tab Id: ${defaultTabId}`);
            StorageKey_TabId.set(defaultTabId);
        }
        ModuleFE_XHR.addDefaultHeader(HeaderKey_DeviceId, () => defaultDeviceId);
        ModuleFE_XHR.addDefaultHeader(HeaderKey_TabId, defaultTabId);
    }
    // ######################## Logic ########################
    getAccounts() {
        return this.cache.all().map(i => cloneObj(i));
    }
    getLoggedStatus = () => this.status;
    isStatus = (status) => asArray(status).includes(this.status);
    __onSessionUpdated() {
        if (!ModuleFE_Session.hasSession())
            return this.setLoggedStatus(LoggedStatus.LOGGED_OUT);
        if (!ModuleFE_Session.isSessionValid())
            return this.setLoggedStatus(LoggedStatus.SESSION_TIMEOUT);
        return this.setLoggedStatus(LoggedStatus.LOGGED_IN);
    }
    setLoggedStatus = (newStatus) => {
        if (this.status === newStatus)
            return;
        const pervStatus = this.status;
        this.status = newStatus;
        this.logInfo(`Login status changes: ${LoggedStatus[pervStatus]} => ${LoggedStatus[newStatus]}`);
        dispatch_onLoginStatusChanged.dispatchUI();
        dispatch_onLoginStatusChanged.dispatchModule();
    };
    composeSAMLUrl = () => {
        const params = new URLSearchParams(window.location.search);
        const paramsObj = {};
        for (const [key, value] of params) {
            paramsObj[key] = value;
        }
        return composeUrl(window.location.origin + window.location.pathname, {
            ...paramsObj,
            [QueryParam_SessionId]: QueryParam_SessionId.toUpperCase(),
        });
    };
    logout = async (url) => {
        await this._v1.logout({}).executeSync();
        dispatcher_onAuthRequired.dispatchModule(undefined);
        if (url)
            return window.location.href = url;
    };
    uploadAccountThumbnail = (e, account) => {
        const input = document.createElement('input');
        input.type = 'file';
        // input.accept = '.jpg,.jpeg,.png';
        input.style.display = 'none';
        input.addEventListener('change', async (e) => {
            const file = input.files[0];
            if (!file)
                return;
            try {
                const hash = await this.encodeFile(file);
                await this._v1.changeThumbnail({ accountId: account._id, hash }).executeSync();
            }
            catch (err) {
                this.logError(err.message, err);
            }
        });
        input.click();
    };
    encodeFile = async (file) => {
        const arrayBuffer = await readFileContent(file);
        if (arrayBuffer.byteLength > 200 * KB)
            throw new Exception('File size exceeds 200KB');
        const buffer = new Uint8Array(arrayBuffer);
        return window.btoa(buffer.reduce((acc, byte) => acc + String.fromCharCode(byte), ''));
    };
    getPasswordAssertionConfig = () => StorageKey_PasswordAssertionConfig.get();
    getCurrentlyLoggedAccount = () => {
        return SessionKeyFE_Account.get();
    };
    // ######################## API Callbacks ########################
    onAccountCreated = async (response) => {
        await this.onEntriesUpdated([response]);
    };
    onThumbnailChanged = async (response) => {
        await this.onEntryUpdated(response.account, response.account);
    };
    onLoginCompletedSAML = async (response) => {
        if (!response.loginUrl)
            return;
        window.location.href = response.loginUrl;
    };
    onPasswordAssertionConfig = async (response) => {
        StorageKey_PasswordAssertionConfig.set(response.config);
    };
}
export const ModuleFE_Account = new ModuleFE_Account_Class();
//# sourceMappingURL=ModuleFE_Account.js.map